import { Fragment, useMemo, useState } from "react";
import { useDateFormatter } from "react-aria";
import { isToday, now } from "@internationalized/date";
import clsx from "clsx";

import { OfficeInformation } from "./OfficeInformation";
import { TimeslotLink } from "./TimeslotLink";
import { Timeframe, useTimetableContext, useTimetableData } from "./Timetable.hooks";
import { TimetableEmptyState } from "./TimetableEmptyState";
import { TimetableHeader } from "./TimetableHeader";

import { Button, ButtonProps } from "~/components/Button";
import { Collapsible } from "~/components/Collapsible";
import { ChevronIcon, DIRECTION } from "~/components/Icons/ChevronIcon";
import { Spinner } from "~/components/Loader";
import { Text } from "~/components/Text";
import { TimetableLoaderData } from "~/modules/timetable/timetable.loader.server";

export function TimetableDesktop(props: TimetableLoaderData) {
  const { days, errors, groups, office, period } = useTimetableData(props);
  const { EVENING, MORNING, NOON } = groups;
  const { timeZone } = useTimetableContext();

  const weekdayFormatter = useDateFormatter({ weekday: "long", timeZone });
  const dayOfMonthFormatter = useDateFormatter({ month: "short", day: "numeric", timeZone });

  const isEmpty =
    MORNING.timeframes.length === 0 &&
    NOON.timeframes.length === 0 &&
    EVENING.timeframes.length === 0;

  return (
    <section className="flex flex-col gap-md" data-component="Timetable">
      <TimetableHeader hasError={errors.TIMESLOT_NOT_AVAILABLE} period={period}>
        {days.map(({ date, id }) => {
          const isPast = !isToday(date, timeZone) && date.compare(now(timeZone)) < 0;

          return (
            <div
              className="flex w-full grow justify-center"
              data-component="TimetableDay"
              data-is-today={isToday(date, timeZone)}
              // TODO hasSlot cf. Mobile
              // data-has-slots={hasSlots}
              key={id}
            >
              <div className="relative flex flex-col text-center">
                <span
                  className={clsx(
                    "text-sm",
                    isPast ? "text-gray-300" : "text-light",
                    // TODO: change to lighter background color: https://skedify.atlassian.net/browse/SKED-9966
                    isToday(date, timeZone) &&
                      "after:absolute after:right-[-6px] after:top-[-6px] after:h-[6px] after:w-[6px] after:rounded-full after:bg-brand-secondary",
                  )}
                  data-component="TimetableDay-Weekday"
                >
                  {weekdayFormatter.format(date.toDate())}
                </span>

                <span
                  className={clsx(
                    "text-center text-lg font-bold lg:text-xl",
                    isPast ? "text-gray-300" : "text-brand",
                  )}
                  data-component="TimetableDay-Date"
                >
                  {dayOfMonthFormatter.format(date.toDate())}
                </span>
              </div>
            </div>
          );
        })}
      </TimetableHeader>

      {isEmpty ? <TimetableEmptyState /> : <TimetableDesktopContent days={days} groups={groups} />}
      {(office.phone || office.email) && (
        <OfficeInformation email={office.email} phone={office.phone} />
      )}
    </section>
  );
}

function TimetableDesktopContent({
  days,
  groups,
}: Pick<ReturnType<typeof useTimetableData>, "days" | "groups">) {
  const [openViews, setOpenViews] = useState<("morning" | "noon" | "evening")[]>([
    "evening",
    "morning",
    "noon",
  ]);

  function toggleView(view: "morning" | "noon" | "evening") {
    setOpenViews((o) => {
      const index = o.indexOf(view);

      if (index === -1) {
        return [...o, view];
      }

      return [...o.slice(0, index), ...o.slice(index + 1)];
    });
  }

  const { EVENING, MORNING, NOON } = groups;

  const views = useMemo<
    {
      id: "morning" | "noon" | "evening";
      name: JSX.Element;
      rows: Timeframe[];
    }[]
  >(() => {
    return [
      {
        id: "morning",
        name: <Text i18nKey="steps.Timetable.DayPart.morning" />,
        rows: MORNING.timeframes,
      },
      {
        id: "noon",
        name: <Text i18nKey="steps.Timetable.DayPart.afternoon" />,
        rows: NOON.timeframes,
      },
      {
        id: "evening",
        name: <Text i18nKey="steps.Timetable.DayPart.evening" />,
        rows: EVENING.timeframes,
      },
    ];
  }, [MORNING.timeframes, NOON.timeframes, EVENING.timeframes]);

  return (
    <>
      <div className="flex flex-col gap-sm" data-component="TimetableContent">
        {views.map((view) => (
          <Collapsible
            className="flex flex-col gap-3xs"
            header={
              <div className="grid grid-cols-7 gap-xs" data-component="TimetableDayPart">
                {days.map((day) => (
                  <DayPartButton
                    isDisabled={view.rows.length === 0}
                    isEmpty={view.rows.length === 0}
                    key={`${view.id}_${day.id}`}
                    onPress={() => toggleView(view.id)}
                  >
                    <ChevronIcon
                      className={clsx(
                        "hidden h-sm w-sm shrink-0 lg:block",
                        view.rows.length !== 0 ? "text-black" : "text-disabled",
                      )}
                      direction={
                        view.rows.length === 0
                          ? DIRECTION.DOWN
                          : openViews.includes(view.id)
                          ? DIRECTION.UP
                          : DIRECTION.DOWN
                      }
                    />
                    {view.name}
                  </DayPartButton>
                ))}
              </div>
            }
            isOpen={openViews.includes(view.id)}
            key={view.id}
          >
            <Slots timeframes={view.rows} />
          </Collapsible>
        ))}
      </div>
    </>
  );
}

interface DayPartButtonProps extends ButtonProps {
  isEmpty?: boolean;
}

function DayPartButton({ isEmpty, ...props }: DayPartButtonProps) {
  return (
    <Button
      data-component="DayPartButton"
      {...props}
      className={clsx(
        "flex w-full flex-1 items-center justify-center gap-xs p-xs text-sm text-gray-500",
        isEmpty && "text-gray-300",
      )}
    />
  );
}

interface SlotsProps {
  timeframes: Timeframe[];
}

function Slots({ timeframes }: SlotsProps) {
  const { timeZone } = useTimetableContext();
  const formatter = useDateFormatter({ timeStyle: "short", timeZone });

  return (
    <div className="grid grid-cols-7 gap-3xs">
      {timeframes.map(({ id, timeSlots }) => (
        <Fragment key={id}>
          {timeSlots.map((slot) => {
            if (slot.data) {
              const formattedStartDate = formatter.formatToParts(slot.data.start.toDate());

              return (
                <div
                  // TODO: change to lighter background color on hover: https://skedify.atlassian.net/browse/SKED-9966
                  className="flex flex-1 items-center justify-center rounded-lg border border-solid text-brand hover:bg-gray-100"
                  data-component="TimetableSlot"
                  key={slot.id}
                >
                  <TimeslotLink
                    className="flex w-full flex-1 items-center justify-center py-xs"
                    slot={slot}
                  >
                    {(isLoading) =>
                      isLoading ? (
                        <div className="my-[-2px]">
                          <Spinner size={Spinner.size.SMALL} />
                        </div>
                      ) : (
                        <>
                          {formattedStartDate.map((part) => (
                            <span
                              className={clsx(
                                "self-baseline leading-none",
                                part.type === "dayPeriod" && "text-xs text-gray-400",
                                part.value === " " && "px-[1px]",
                              )}
                              key={part.type + part.value}
                            >
                              {part.value}
                            </span>
                          ))}
                        </>
                      )
                    }
                  </TimeslotLink>
                </div>
              );
            }

            return (
              <div
                className="flex w-full flex-1 rounded-lg border border-solid bg-gray-50"
                data-component="TimetableEmptySlot"
                key={slot.id}
              />
            );
          })}
        </Fragment>
      ))}
    </div>
  );
}
